import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { FacebookCircleIcon, InstagramIcon, TikTokIcon } from "./SocialIcons";
import type { TranslationKey } from "@/lib/i18n/locales";
import { ANDROID_APP_URL, IOS_APP_URL } from "@/lib/storeLinks";
import { cn } from "@/lib/utils";

const APP_BADGES = [
  {
    storeUrl: IOS_APP_URL,
    src: "/app-store-badge.svg",
    altKey: "footer_app_store_alt",
    width: 120,
  },
  {
    storeUrl: ANDROID_APP_URL,
    src: "/google-play-badge.svg",
    altKey: "footer_google_play_alt",
    width: 135,
  },
] satisfies ReadonlyArray<{
  storeUrl: string;
  src: string;
  altKey: TranslationKey;
  width: number;
}>;

const SOCIAL_LINKS = [
  {
    labelKey: "footer_instagram_aria",
    href: "https://www.instagram.com/app.shorts/",
    Icon: InstagramIcon,
  },
  {
    labelKey: "footer_tiktok_aria",
    href: "https://www.tiktok.com/@shortsapp",
    Icon: TikTokIcon,
  },
  {
    labelKey: "footer_facebook_aria",
    href: "https://www.facebook.com/profile.php?id=61572673767899",
    Icon: FacebookCircleIcon,
  },
] satisfies ReadonlyArray<{
  labelKey: TranslationKey;
  href: string;
  Icon: React.FC<React.SVGProps<SVGSVGElement>>;
}>;

interface FooterLinksProps {
  className?: string;
  // Force a single column on every viewport — used inside the burger menu,
  // whose container is narrow even on desktop. The footer leaves it false to
  // get the responsive 4-column desktop layout.
  stacked?: boolean;
  // Fired when an internal nav link is clicked — the burger menu uses it to
  // close the Sheet on navigation (not on badge/social clicks).
  onNavigate?: () => void;
}

// Shared content for the footer and the Header burger menu: the four link
// groups + copyright. Keeping it in one place keeps both in sync.
const FooterLinks: React.FC<FooterLinksProps> = ({
  className,
  stacked,
  onNavigate,
}) => {
  const { t } = useTranslation();

  return (
    <div className={className}>
      <div
        className={cn(
          "flex flex-col gap-10",
          !stacked && "lg:flex-row lg:justify-between lg:gap-8",
        )}
      >
        <FooterColumn title={t("footer_about_header")}>
          <FooterLink to="/terms" onClick={onNavigate}>
            {t("footer_terms_link")}
          </FooterLink>
          <FooterLink to="/privacy" onClick={onNavigate}>
            {t("footer_privacy_link")}
          </FooterLink>
        </FooterColumn>

        <FooterColumn title={t("footer_support_header")}>
          <FooterLink to="/money-back-policy" onClick={onNavigate}>
            {t("footer_refund_link")}
          </FooterLink>
          <FooterLink to="/faq" onClick={onNavigate}>
            {t("footer_faq_link")}
          </FooterLink>
          <FooterLink to="/contact-us" onClick={onNavigate}>
            {t("footer_contact_link")}
          </FooterLink>
        </FooterColumn>

        <FooterColumn title={t("footer_download_header")}>
          {APP_BADGES.map(({ storeUrl, src, altKey, width }) => (
            <a key={altKey} href={storeUrl} target="_blank" rel="noreferrer">
              <img
                src={src}
                alt={t(altKey)}
                className="h-10 w-auto"
                width={width}
                height={40}
              />
            </a>
          ))}
        </FooterColumn>

        <FooterColumn title={t("footer_follow_header")}>
          <div className="flex items-center gap-4">
            {SOCIAL_LINKS.map(({ labelKey, href, Icon }) => (
              <a
                key={labelKey}
                href={href}
                target="_blank"
                rel="noreferrer"
                aria-label={t(labelKey)}
                className="text-neutral-30 transition-colors hover:text-white"
              >
                <Icon />
              </a>
            ))}
          </div>
        </FooterColumn>
      </div>

      <p className="text-neutral-30 mt-12 text-xs">
        {t("footer_copyright", { year: new Date().getFullYear() })}
      </p>
    </div>
  );
};

const FooterColumn: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <div className="flex flex-col gap-4">
    <h3 className="font-semibold">{title}</h3>
    {children}
  </div>
);

const FooterLink: React.FC<{
  to: string;
  onClick?: () => void;
  children: React.ReactNode;
}> = ({ to, onClick, children }) => (
  <Link
    to={to}
    onClick={onClick}
    className="text-neutral-30 transition-colors hover:text-white"
  >
    {children}
  </Link>
);

export default FooterLinks;
